import React from 'react';
import { useLocation, useParams, matchPath } from 'react-router-dom';
import SeoHead, { buildProductJsonLd, buildBreadcrumbJsonLd, buildBrandJsonLd, buildItemListJsonLd, cleanDescription } from './SeoHead';
import { PRODUCTS, COLLECTIONS, COLLECTIONS_BY_BRAND } from '../mock';
import { getBrandBySlug } from '../data/brands';

/**
 * RouteAwareSeo
 * =============
 * Single point of <head> management for the entire app. Mounted ONCE at the
 * App level, it reads the current location and emits exactly one <SeoHead/>
 * for the matched route.
 *
 * Why centralized? Putting <SeoHead/> inside individual page components led
 * to React 19's native metadata hoisting accumulating tags from multiple
 * components when the prerender headless browser rendered intermediate
 * states, producing duplicate <title>/<meta> entries in the saved HTML.
 *
 * Adding a new route? Add a branch to the switch below.
 */

const slugifyBrand = (b) =>
  b.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const STATIC_ROUTES = {
  '/':            { title: 'Premium Body Kits & Forged Wheels', description: "Canada's largest selection of premium body kits, dry carbon fiber, forged wheels and custom fabrication. Fortune Auto Design — precision, performance, style." },
  '/shop':        { title: 'Shop by Vehicle', description: 'Browse premium body kits, lips, hoods and aero parts by your vehicle make and model.' },
  '/wheels':      { title: 'Fortune Forged Wheels — Monoblock & 2-Piece', description: 'Bespoke forged wheels — Monoblock 1-piece from $595, 2-Piece from $945. 12 designs, custom fitment & finish, made-to-order in Canada.' },
  '/body-kits':   { title: 'Body Kits — Widebody, Aero, Lips & Spoilers', description: 'Dry carbon fiber, FRP and forged carbon body kits for BMW, Tesla, Porsche, Audi and more. Premium fitment, made-to-order.' },
  '/about':       { title: 'About', description: "Canadian-based studio specializing in dry carbon fiber, body kits, forged wheels and custom fabrication for the world's most iconic performance cars." },
  '/services':    { title: 'Services — Install, Paint & Custom Fab', description: 'Professional installation, paint matching, custom fabrication and bespoke wheel builds at our Canadian facility.' },
  '/process':     { title: 'Our Process — From Order to Install', description: 'How we engineer, source, ship and install your Fortune body kit or forged wheels.' },
  '/faq':         { title: 'FAQ — Shipping, Returns, Fitment', description: 'Frequently asked questions about shipping, fitment, lead times, returns and custom orders.' },
  '/contact':     { title: 'Contact Us', description: 'Get a quote, ask about fitment, or book a consultation. Toronto, Canada — shipped worldwide.' },
  '/terms':       { title: 'Terms & Privacy', description: 'Terms of service and privacy policy for Fortune Auto Design.' },
  '/checkout':    { title: 'Submit Your Order', description: 'Submit your order request — no payment now. Our team reviews availability and emails a secure invoice to pay by card. Fortune Auto Design.' },
  '/inquiry':     { title: 'Build Inquiry', description: 'Submit your build inquiry — Fortune Auto Design.' },
  '/thank-you':   { title: 'Thank You', description: 'Your order is confirmed.' },
  '/search':      { title: 'Search Parts', description: 'Search 500+ body kit parts, aero, hoods, lips and forged wheels across every brand we carry.' },
  '/palettes':    { title: 'Custom Color Palettes', description: 'Browse paint and finish palettes for your Fortune body kit or forged wheel build.' },
  '/hero-options':{ title: 'Get Started — Body Kits or Forged Wheels', description: "Premium dry carbon, forged carbon, and bespoke wheels for the world's most iconic chassis." },
};

const RouteAwareSeo = () => {
  const { pathname } = useLocation();

  // 1) /product/:slug
  const productMatch = matchPath('/product/:slug', pathname);
  if (productMatch) {
    const slug = productMatch.params.slug;
    const product = PRODUCTS.find((p) => p.slug === slug || p.id === slug);
    if (product) {
      const collection = COLLECTIONS.find((c) => c.id === product.collectionId);
      const brandName = collection?.brand || product.brand;
      const crumbs = [{ name: 'Home', url: '/' }];
      if (brandName) crumbs.push({ name: brandName, url: `/shop/${slugifyBrand(brandName)}` });
      if (collection) crumbs.push({ name: collection.name, url: `/collections/${collection.id}` });
      crumbs.push({ name: product.name, url: `/product/${product.slug || product.id}` });
      return (
        <SeoHead
          title={product.seoTitle || product.name}
          description={cleanDescription(product.seoDescription || product.description || product.descriptionHtml) || `Premium ${product.category} from ${product.brand}. Made-to-order at Fortune Auto Design.`}
          path={`/product/${product.slug || product.id}`}
          image={(product.images && product.images[0]) || undefined}
          type="product"
          jsonLd={[buildProductJsonLd(product), buildBreadcrumbJsonLd(crumbs)]}
        />
      );
    }
    return <SeoHead title="Product Not Found" path={pathname} noindex />;
  }

  // 2) /collections/:collectionId or /collection/:collectionId
  const collectionMatch =
    matchPath('/collections/:collectionId', pathname) ||
    matchPath('/collection/:collectionId', pathname);
  if (collectionMatch) {
    const cid = collectionMatch.params.collectionId;
    const col = COLLECTIONS.find((c) => c.id === cid);
    if (col) {
      const items = PRODUCTS.filter((p) => p.collectionId === cid);
      const crumbs = [{ name: 'Home', url: '/' }];
      if (col.brand) crumbs.push({ name: col.brand, url: `/shop/${slugifyBrand(col.brand)}` });
      crumbs.push({ name: col.name, url: `/collections/${col.id}` });
      return (
        <SeoHead
          title={col.seoTitle || `${col.name} — ${items.length} ${items.length === 1 ? 'Part' : 'Parts'}`}
          description={cleanDescription(col.seoDescription || col.descriptionHtml) || cleanDescription(`${col.tagline || col.name} — shop ${items.length} premium body kits, lips, hoods and aero parts at Fortune Auto Design. ${col.years || ''}`)}
          path={`/collections/${col.id}`}
          image={col.image}
          jsonLd={buildBreadcrumbJsonLd(crumbs)}
        />
      );
    }
    return <SeoHead title="Collection Not Found" path={pathname} noindex />;
  }

  // 3) /brands/:brandSlug  (manufacturer / carbon brand hub)
  const brandHubMatch = matchPath('/brands/:brandSlug', pathname);
  if (brandHubMatch) {
    const brand = getBrandBySlug(brandHubMatch.params.brandSlug);
    if (brand) {
      const items = PRODUCTS.filter((p) => p.brand === brand.name);
      const crumbs = [
        { name: 'Home', url: '/' },
        { name: 'Brands', url: '/shop' },
        { name: brand.name, url: `/brands/${brand.slug}` },
      ];
      return (
        <SeoHead
          title={brand.title}
          description={cleanDescription(brand.metaDescription)}
          path={`/brands/${brand.slug}`}
          jsonLd={[
            buildBrandJsonLd(brand),
            buildBreadcrumbJsonLd(crumbs),
            buildItemListJsonLd(items),
          ]}
        />
      );
    }
  }

  // 4) /shop/:brand
  const brandMatch = matchPath('/shop/:brand', pathname);
  if (brandMatch) {
    const brandSlug = brandMatch.params.brand;
    const brand = Object.keys(COLLECTIONS_BY_BRAND).find((b) => slugifyBrand(b) === brandSlug);
    if (brand) {
      const models = COLLECTIONS_BY_BRAND[brand] || [];
      return (
        <SeoHead
          title={`${brand} Body Kits, Aero & Forged Wheels`}
          description={`Browse every ${brand} chassis we outfit — carbon fiber lips, hoods, widebody kits, spoilers and forged wheels. ${models.length} models available at Fortune Auto Design.`}
          path={`/shop/${brandSlug}`}
        />
      );
    }
  }

  // 4) Static routes
  const cfg = STATIC_ROUTES[pathname];
  if (cfg) {
    // Enrich the Fortune Forged Wheels hub (/wheels) with structured data so it
    // can rank as a brand hub for "Fortune Forged Wheels" queries.
    if (pathname === '/wheels') {
      const wheels = PRODUCTS.filter((p) => p.collectionId === 'fortune-forged-wheels');
      const crumbs = [
        { name: 'Home', url: '/' },
        { name: 'Fortune Forged Wheels', url: '/wheels' },
      ];
      return (
        <SeoHead
          title={cfg.title}
          description={cfg.description}
          path={pathname}
          jsonLd={[
            {
              '@context': 'https://schema.org',
              '@type': 'Brand',
              name: 'Fortune Forged Wheels',
              description: cfg.description,
              url: 'https://fortuneautodesign.com/wheels',
            },
            buildBreadcrumbJsonLd(crumbs),
            buildItemListJsonLd(wheels),
          ]}
        />
      );
    }
    return <SeoHead title={cfg.title} description={cfg.description} path={pathname} />;
  }

  // 5) /admin/* — internal tools. Hide from search engines.
  if (pathname.startsWith('/admin')) {
    return (
      <SeoHead
        title="Admin"
        description="Internal admin tools."
        path={pathname}
        noindex
      />
    );
  }

  // 6) Default fallback (rare; Shopify-redirect route etc.)
  return <SeoHead path={pathname} />;
};

export default RouteAwareSeo;
